/**
 * #1124 — formatting for the Data pressure card and its Copy report.
 * Unavailable is a dash, never 0.
 */
import type { CoalescedReading, ChurnReading, FeedConnections, FeedPressureSample, FeedVenue, HoldingsReading } from '@/hooks/useFeedPressure';
import type { PageMemoryReading } from '@/lib/telemetry/pageMemory';
import type { MarketViewGroup } from '@/lib/telemetry/viewGroups';
import {
  MEASUREMENT_LINE, formatCharsPerSec, formatLivenessLine, formatSiteLine, formatSourceAgeLine, formatTransportLine, formatViewLine,
  type LivenessReading, type ReportView, type SourceAgeReading, type TransportCounts,
} from '@/lib/telemetry/reportContext';

const DASH = '—';

export const VENUE_LABEL: Record<FeedVenue, string> = {
  kalshi: 'Kalshi',
  polyIntl: 'Poly INTL',
  polyUs: 'Poly US',
  odds: 'Odds',
};

export function formatRate(n: number | null): string {
  if (n == null || !Number.isFinite(n)) return DASH;
  if (n > 0 && n < 1) return n.toFixed(1);
  return Math.round(n).toLocaleString('en-US');
}

export function formatPct(n: number | null): string {
  return n == null || !Number.isFinite(n) ? DASH : `${Math.round(n)}%`;
}

/** Compact lag for the collapsed line: ms under a second, then seconds. */
export function formatLagShort(lagMs: number | null): string {
  if (lagMs == null) return DASH;
  return lagMs < 1000 ? `${Math.round(lagMs)} ms` : `${(lagMs / 1000).toFixed(1)} s`;
}

export function formatLag(lagMs: number | null): string {
  return lagMs == null ? DASH : `${Math.round(lagMs).toLocaleString('en-US')} ms`;
}

const KB = 1024;
const MB = KB * 1024;
const GB = MB * 1024;

function bytesText(n: number): string {
  const a = Math.abs(n);
  return a >= GB ? `${(a / GB).toFixed(1)} GB` : `${Math.round(a / MB).toLocaleString('en-US')} MB`;
}

/** Chrome rounds its heap reading, so the card never shows it as exact. */
export function formatAboutBytes(n: number | null): string {
  return n == null || !Number.isFinite(n) ? DASH : `about ${bytesText(n)}`;
}

/** Page age: short for the card ("3 h"), long for the report ("3 h 04 m"). */
export function formatPageAge(ms: number, long = false): string {
  if (ms < 60_000) return 'under a minute';
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  return long ? `${h} h ${String(min % 60).padStart(2, '0')} m` : `${h} h`;
}

function growthText(m: PageMemoryReading, long: boolean): string {
  const since = `since the page opened ${formatPageAge(m.pageAgeMs, long)} ago`;
  if (m.growthBytes == null) return `opened ${formatPageAge(m.pageAgeMs, long)} ago`;
  if (m.sameAsStart) return `about the same ${since}`;
  return `${m.growthBytes >= 0 ? 'up' : 'down'} ${bytesText(m.growthBytes)} ${since}`;
}

/** Heap and growth on one line (the report). */
export function formatMemory(m: PageMemoryReading | null, long = false): string {
  return m == null ? DASH : `${formatAboutBytes(m.usedBytes)} · ${growthText(m, long)}`;
}

/** The card's second memory line: growth since the page opened. */
export function formatMemoryGrowth(m: PageMemoryReading): string {
  return growthText(m, false);
}

export const MEMORY_CAVEAT = 'Chrome refreshes this about every 20 min in steps of about 6 %';

function sportsText(sports: MarketViewGroup['sports'], max: number, withMore = true): string {
  if (sports === 'all') return 'all sports';
  const shown = sports.slice(0, max).join(', ');
  return withMore && sports.length > max ? `${shown} +${sports.length - max}` : shown;
}

function marketViewText(g: MarketViewGroup, maxSports: number, withGames: boolean, moreSports: boolean): string {
  const parts: string[] = [];
  const sports = sportsText(g.sports, maxSports, moreSports);
  if (sports) parts.push(sports);
  const scoped = g.sports !== 'all' && g.sports.length === 0;
  // A ticker-scoped view (positions, open books) reads as its tickers, never as
  // empty sports or "0 games". The card line shows them only when it has no sports.
  if (g.tickers != null && (withGames || scoped)) parts.push(`${g.tickers} ticker${g.tickers === 1 ? '' : 's'}`);
  else if (withGames && g.games != null && g.games > 0) parts.push(`${g.games} game${g.games === 1 ? '' : 's'}`);
  return parts.length > 0 ? `${g.label} ${parts.join(', ')}` : g.label;
}

/** The first `max` views, then "N more". The card narrows each view's text
 *  (fewer sports, no games) to fit the rail; the hover and report don't. */
export function formatMarketViews(
  groups: readonly MarketViewGroup[], max: number, opts: { sports?: number; games?: boolean; moreSports?: boolean } = {},
): string {
  const parts = groups.slice(0, max).map((g) => marketViewText(g, opts.sports ?? 3, opts.games ?? true, opts.moreSports ?? true));
  if (groups.length > max) parts.push(`${groups.length - max} more`);
  return parts.join(' · ');
}

export function formatTopCounts(items: readonly { label: string; books: number }[], max: number): string {
  const parts = items.slice(0, max).map((i) => `${i.label} ${i.books.toLocaleString('en-US')}`);
  if (items.length > max) parts.push(`${items.length - max} more`);
  return parts.join(' · ');
}

/** "16:58Z" in the report (UTC, like its header); the card shows local time. */
function clockUtc(ms: number): string {
  return `${new Date(ms).toISOString().slice(11, 16)}Z`;
}

/** Local HH:MM for the card. */
export function formatClockLocal(ms: number): string {
  return new Date(ms).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export function formatBlocked(blockedMs: number): string {
  return `${(blockedMs / 1000).toFixed(1)} s`;
}

function holdingsLines(h: HoldingsReading | null): string[] {
  if (h == null) return ['Markets open: — (not sampled yet)'];
  const n = (x: number) => x.toLocaleString('en-US');
  const sports = h.marketsBySport.map((b) => `${b.sport.toUpperCase()} ${n(b.markets)}`).join(' · ');
  return [
    `Views asking for markets: ${h.marketViews.length}${h.marketViews.length > 0 ? ` (${formatMarketViews(h.marketViews, h.marketViews.length)})` : ''}`,
    `Books needed: ${n(h.booksNeeded)}${h.bookViews.length > 0 ? ` (${formatTopCounts(h.bookViews, h.bookViews.length)})` : ''}`,
    `Delivered to this tab: ${n(h.marketsDelivered)} markets · ${n(h.booksDelivered)} books${sports ? ` (${sports}; a market can count under two sports)` : ''}`,
    `Books routed per connection: ${h.booksByConnection.length > 0 ? formatTopCounts(h.booksByConnection, h.booksByConnection.length) : 'none'}`,
    `g1 windows sharing a feed: ${h.windows == null ? `${DASH} (no shared worker)` : `${h.windows} (a closed one can count for up to 10 min)`}`,
  ];
}

function memoryLine(m: PageMemoryReading | null): string {
  if (m == null) return "Memory: — (this browser doesn't report page memory)";
  const slope = m.slopeBytesPerHour == null ? '' : ` · ${m.slopeBytesPerHour >= 0 ? '+' : '-'}${bytesText(m.slopeBytesPerHour)}/h`;
  return `Memory: ${formatMemory(m, true)}${slope} (coarse: ${MEMORY_CAVEAT})`;
}

/** Its own section: described, never a verdict on what caused a delay. */
function mainThreadLine(sample: FeedPressureSample): string {
  if (sample.hidden) return "This tab's main thread: — (tab hidden)";
  const t = sample.longTasks;
  if (t == null) return "This tab's main thread: — (this browser doesn't report long tasks)";
  return `This tab's main thread (described, not diagnosed): blocked ${formatBlocked(t.blockedMs)} of the last 10 s (${t.count} tasks over 50 ms). `
    + 'A blocked main thread delays everything this tab draws; this report does not decide whether it caused a delay.';
}

export function formatIn5m(n: number, capped: boolean): string {
  return `${n}${capped ? '+' : ''} in 5 min`;
}

function churnLine(c: ChurnReading | null): string {
  if (c == null) return 'Connection resets seen by this tab: — · Resnapshots requested: —';
  return `Connection resets seen by this tab: ${c.resets} (${formatIn5m(c.resetsIn5m, c.resetsIn5mCapped)}; ${c.upstream} upstream) · `
    + `Resnapshots requested: ${c.resnapshots} (${formatIn5m(c.resnapshotsIn5m, c.resnapshotsIn5mCapped)})`;
}

function coalescedLine(c: CoalescedReading | null): string {
  if (c == null) return 'Books coalesced by hydra: —';
  if (c.connections === 0) return `Books coalesced by hydra: not observed in this window (${c.total} connections)`;
  const rows = c.rows.map((r) => `${r.label}${r.sinceMs == null ? '' : ` since ${clockUtc(r.sinceMs)}`}`).join(' · ');
  return `Books coalesced by hydra: ${c.connections} of ${c.total} connections${c.paused ? ', books paused on at least one' : ''} (${rows})`;
}

/** Read once, when Copy is clicked: no sampler, poll or fetch behind any of it. */
export interface ReportContextReading {
  /** The page's own host (location.host). */
  host: string;
  /** The build the site serves now (buildUpdateStore), null before its first read. */
  served: string | null;
  transport: TransportCounts;
  liveness: LivenessReading;
  /** Game Detail's books only; null on a view with no single game. */
  sourceAge: SourceAgeReading | null;
}

/** `'opening'`: the list is open but its first sample hasn't landed, so the report waits for it rather than reading twice. */
export type ReportConnections = FeedConnections | null | 'opening';

function connectionsLine(connections: ReportConnections): string {
  if (connections === 'opening') return 'Connections: — (the list is opening; no sample yet)';
  if (connections == null || connections.total === 0) return 'Connections: — (no feed connections yet)';
  const parts = connections.rows.map((r) => {
    const flag = r.backpressureLevel >= 2 ? ' books paused' : r.backpressureLevel >= 1 ? ' coalesced' : '';
    const since = flag && r.backpressureSinceMs != null ? ` since ${clockUtc(r.backpressureSinceMs)}` : '';
    const perFrame = r.framesPerSec > 0 ? ` ~${Math.round(r.bytesPerSec / r.framesPerSec).toLocaleString('en-US')} chars/frame` : '';
    // A direct socket's window rolls on read, so its span is whatever passed since the last reader.
    const span = r.transport === 'direct' && r.windowMs != null ? ` (direct socket, over ${Math.round(r.windowMs / 1000)} s)` : '';
    return `${r.label} ${formatRate(r.framesPerSec)}/s ${formatCharsPerSec(r.bytesPerSec)} JSON chars/s${perFrame}${flag}${since}${span}`;
  });
  if (connections.idle > 0) parts.push(`${connections.idle} idle`);
  if (connections.starting > 0) parts.push(`${connections.starting} starting`);
  if (connections.reconnecting > 0) parts.push(`${connections.reconnecting} reconnecting`);
  parts.push(`${connections.total} in all`);
  const window = connections.rows.some((r) => r.transport === 'direct') ? 'worker 5 s window unless marked direct' : 'worker 5 s window';
  return `Connections (${window}; ordered by frame count, which is not a cause): ${parts.join(' · ')}`;
}

export function buildPressureReport(input: {
  sample: FeedPressureSample;
  connections: ReportConnections;
  view: ReportView;
  context: ReportContextReading;
  build: string;
}): string {
  const { sample, connections, view, context, build } = input;
  const when = sample.sampledAtMs == null ? 'not sampled yet' : new Date(sample.sampledAtMs).toISOString().replace(/\.\d{3}Z$/, 'Z');
  const lines = [
    `g1 data pressure · ${when}`,
    formatSiteLine({ host: context.host, build, served: context.served }),
    formatViewLine(view),
    formatTransportLine(context.transport),
    MEASUREMENT_LINE,
    formatLivenessLine(context.liveness),
    formatSourceAgeLine(context.sourceAge),
    ...holdingsLines(sample.holdings),
    memoryLine(sample.memory),
    churnLine(sample.churn),
    coalescedLine(sample.coalesced),
    "Hydra coalescing reports are kept until hydra's socket closes and say it coalesced from that time, not that it still is; a new socket starts unreported, so absence does not prove recovery.",
    'This tab, last second / peak over 60 s:',
  ];
  // While hidden each "last second" is a 6 s average, so the rates aren't shown.
  const rate = (n: number | null) => formatRate(sample.hidden ? null : n);
  for (const v of sample.venues) {
    const label = `  ${VENUE_LABEL[v.venue]}`.padEnd(13);
    if (v.venue === 'odds') {
      lines.push(`${label}markets ${rate(v.marketsPerSec)}/s (OddsPapi ${rate(v.oddspapiPerSec ?? null)})  peak ${formatRate(v.peakPerSec)}`);
    } else {
      lines.push(`${label}books ${rate(v.booksPerSec)}/s  markets ${rate(v.marketsPerSec)}/s  shown ${formatPct(sample.hidden ? null : v.shownPct)}  peak ${formatRate(v.peakPerSec)}`);
    }
  }
  lines.push(`Tab behind the feed worker: ${formatLag(sample.lagMs)} (5 s sample; worker → this tab only)`);
  if (sample.hidden) lines.push('Tab hidden: rates not shown, peak frozen until it is visible again');
  lines.push(connectionsLine(connections));
  lines.push(mainThreadLine(sample));
  return lines.join('\n');
}
